'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Room, RoomEvent } from 'livekit-client';
import { motion } from 'motion/react';
import { RoomAudioRenderer, RoomContext, StartAudio } from '@livekit/components-react';
import { ErrorMessage } from '@/components/embed-popup/error-message';
import { PopupView } from '@/components/embed-popup/popup-view';
import { Trigger } from '@/components/embed-popup/trigger';
import { WelcomeForm } from '@/components/embed-popup/welcome-form';
import useConnectionDetails from '@/hooks/use-connection-details';
const PopupViewMotion = motion.create(PopupView);
function AgentClient({ appConfig, restaurantHash }) {
    const isAnimating = useRef(false);
    const room = useMemo(() => new Room(), []);
    const [popupOpen, setPopupOpen] = useState(false);
    const [error, setError] = useState(null);
    const [userMetadata, setUserMetadata] = useState(undefined);
    const [showForm, setShowForm] = useState(true);
    const { connectionDetails, refreshConnectionDetails, existingOrRefreshConnectionDetails } = useConnectionDetails(appConfig, userMetadata, restaurantHash);
    const handleTogglePopup = () => {
        if (isAnimating.current) {
            // prevent re-opening before room has disconnected
            return;
        }
        setError(null);
        setPopupOpen((open) => !open);
    };
    const handlePanelAnimationStart = () => {
        isAnimating.current = true;
    };
    const handlePanelAnimationComplete = () => {
        isAnimating.current = false;
        if (!popupOpen && room.state !== 'disconnected') {
            room.disconnect();
            setShowForm(true);
            setUserMetadata(undefined);
        }
    };
    const handleFormSubmit = (data) => {
        setError(null); // Clear any previous errors
        setUserMetadata(data);
        setShowForm(false);
    };
    useEffect(() => {
        const onDisconnected = () => {
            setPopupOpen(false);
            refreshConnectionDetails();
        };
        const onMediaDevicesError = (error) => {
            setError({
                title: 'Error con los dispositivos de audio',
                description: `${error.name}: ${error.message}`,
            });
        };
        room.on(RoomEvent.MediaDevicesError, onMediaDevicesError);
        room.on(RoomEvent.Disconnected, onDisconnected);
        return () => {
            room.off(RoomEvent.Disconnected, onDisconnected);
            room.off(RoomEvent.MediaDevicesError, onMediaDevicesError);
        };
    }, [room, refreshConnectionDetails]);
    useEffect(() => {
        if (!popupOpen || showForm || !userMetadata) {
            return;
        }
        // Don't check for connectionDetails here - let the connect function handle it
        if (room.state !== 'disconnected') {
            return;
        }
        const connect = async () => {
            // Clear any previous errors before connecting
            setError(null);
            const connectPromises = [
                existingOrRefreshConnectionDetails().then((connectionDetails) => {
                    if (!connectionDetails) {
                        throw new Error('No se pudieron obtener los detalles de conexión');
                    }
                    return room.connect(connectionDetails.serverUrl, connectionDetails.participantToken);
                }),
            ];
            if (appConfig.supportsAudioInput) {
                connectPromises.push(room.localParticipant.setMicrophoneEnabled(true, undefined, {
                    preConnectBuffer: appConfig.isPreConnectBufferEnabled,
                }));
            }
            Promise.all(connectPromises)
                .then(() => {
                // Connection successful, clear any errors
                setError(null);
            })
                .catch((error) => {
                if (error instanceof Error) {
                    console.error('Error connecting to agent:', error);
                    setError({
                        title: 'Error al conectar con el asistente',
                        description: `${error.name}: ${error.message}`,
                    });
                }
            });
        };
        connect();
    }, [
        room,
        popupOpen,
        showForm,
        userMetadata,
        existingOrRefreshConnectionDetails,
        appConfig.isPreConnectBufferEnabled,
    ]);
    return (_jsxs(RoomContext.Provider, { value: room, children: [_jsx(RoomAudioRenderer, {}), _jsx(StartAudio, { label: "Start Audio" }), _jsx(Trigger, { error: error, popupOpen: popupOpen, onToggle: handleTogglePopup }), _jsx(motion.div, { inert: !popupOpen, initial: {
                    opacity: 0,
                    translateY: 8,
                }, animate: {
                    opacity: popupOpen ? 1 : 0,
                    translateY: popupOpen ? 0 : 8,
                }, transition: {
                    type: 'spring',
                    bounce: 0,
                    duration: popupOpen ? 1 : 0.2,
                }, onAnimationStart: handlePanelAnimationStart, onAnimationComplete: handlePanelAnimationComplete, className: "fixed right-32 bottom-20 left-4 z-50 md:left-auto", children: _jsx("div", { className: "bg-white border-separator1 dark:border-separator2 ml-auto h-[480px] w-full rounded-[28px] border border-solid drop-shadow-2xl md:w-[360px]", children: _jsxs("div", { className: "relative h-full w-full", children: [_jsx(ErrorMessage, { error: error }), !error && showForm && _jsx(WelcomeForm, { onSubmit: handleFormSubmit }), !error && !showForm && (_jsx(PopupViewMotion, { appConfig: appConfig, initial: { opacity: 0 }, animate: { opacity: 1 }, transition: {
                                    duration: 0.2,
                                    ease: 'linear',
                                }, disabled: !popupOpen, sessionStarted: popupOpen && !showForm, onEmbedError: setError, className: "absolute inset-0 bg-white dark:bg-bg2" }))] }) }) })] }));
}
export default AgentClient;
